import { addDays, differenceInMinutes, isBefore, isValid, parseISO, startOfDay } from "date-fns";
import { castDate } from "@caredna-marlin/utils/dates";
import {
  CalendarBlockEventStatusEnum,
  CommandIndexCareRequirementVisitsQuery,
  CommandIndexProvidersQuery,
  VisitEventStatusEnum,
  VisitResourceErrandStatusEnum,
} from "~/graphql/__generated__/graphql";
import {
  LocalCalendarBlock,
  LocalShift,
  LocalVisit,
  LocalVisitResource,
  SkippedVisitResource,
} from "~/hooks/visitDragDropReducer";
import { OrgConfig } from "~/providers/OrgConfigProvider";
import { createVisitEventFromResource, LocalVisitRequest } from "~/utils/eventsHelper";
import { buildCareRequirementWarningsByVisitResourceId, getVisitWarning } from "~/views/command/visitWarningUtils";
import { calendarBlockIsFilter } from "@caredna-marlin/utils/calendarBlockHelper";
import { compact, map, sumBy, uniq } from "lodash";
import { visitResourceStatus } from "~/utils/statuses";

type VisitResourceOverlapDetail = {
  visitResource: LocalVisitResource;
  overlappingVisitResource?: LocalVisitResource;
  overlappingCalendarBlock?: LocalCalendarBlock;
};

type VisitResourceIssue = {
  visitResource: LocalVisitResource;
  overlaps: string[];
  skipped: string[];
  warnings: string[];
};

export type ConsolidatedVisitResourceIssue = VisitResourceIssue & {
  allReasons: string[];
  mainText: string;
  additionalCount: number;
};

export type OptimizationIssueStats = {
  totalOptimizedVisits: number;
  originalVisitsWithIssuesCount: number;
  newVisitsWithIssuesCount: number;
  originalTotalDriveTime: number;
  newTotalDriveTime: number;
  originalAvailable60Min: number | "-";
  newAvailable60Min: number | "-";
  consolidatedVisitResourceIssues: ConsolidatedVisitResourceIssue[];
};

type OptimizationIssueStatsInput = {
  date: Date | string;
  originalVisitResources?: LocalVisitResource[];
  localVisitResources: LocalVisitResource[];
  localVisits: LocalVisit[];
  localVisitRequests: LocalVisitRequest[];
  providers: CommandIndexProvidersQuery["commandIndexProviders"]["providers"];
  localShifts: LocalShift[];
  localCalendarBlocks: LocalCalendarBlock[];
  careRequirementVisits: CommandIndexCareRequirementVisitsQuery["visit"];
  skippedVisitResources?: SkippedVisitResource[];
  orgConfig: OrgConfig;
};

type TimeRange = {
  startTime: Date;
  endTime: Date;
};

const trimRangeToBounds = (range: TimeRange, bounds: TimeRange): TimeRange | null => {
  const startTime = range.startTime > bounds.startTime ? range.startTime : bounds.startTime;
  const endTime = range.endTime < bounds.endTime ? range.endTime : bounds.endTime;

  if (!isBefore(startTime, endTime)) return null;

  return { startTime, endTime };
};

const getHourSlotsInRange = (range: TimeRange): number => {
  const { startTime: start, endTime: end } = range;

  if (!isBefore(start, end)) return 0;

  const diffMinutes = differenceInMinutes(end, start);
  const slots = Math.floor(diffMinutes / 60);

  return slots > 0 ? slots : 0;
};

const getShiftBlockers = <T extends {
  providerId: number | null;
  startTime: string;
  endTime: string;
  eventStatus?: CalendarBlockEventStatusEnum;
  errandStatus?: VisitResourceErrandStatusEnum | null;
  visit?: { eventStatus?: VisitEventStatusEnum };
}>(
  resources: T[],
  shift: LocalShift
): T[] => {
  const providerId = shift.providerId ?? shift.provider?.id;
  const shiftStart = parseISO(shift.startTime);
  const shiftEnd = parseISO(shift.endTime);

  if (!providerId || !isValid(shiftStart) || !isValid(shiftEnd)) return [];

  return resources.filter((resource) => {
    if (resource.providerId !== providerId) return false;
    const resourceStart = parseISO(resource.startTime);
    const resourceEnd = parseISO(resource.endTime);
    if (!isValid(resourceStart) || !isValid(resourceEnd) || resource.eventStatus === "canceled") {
      return false;
    }
    const visitEventStatus = resource.visit?.eventStatus;
    if (visitEventStatus && visitResourceStatus({
      errandStatus: resource.errandStatus,
      visit: { eventStatus: visitEventStatus },
    }) === VisitEventStatusEnum.Canceled) {
      return false;
    }

    return resourceStart < shiftEnd && shiftStart < resourceEnd;
  });
};

const toTimeRangesFromVisitResources = (visitResources: LocalVisitResource[]): TimeRange[] => {
  return compact(
    visitResources.map((resource) => {
      const start = parseISO(resource.startTime);
      const end = parseISO(resource.endTime);
      if (!isValid(start) || !isValid(end) || !isBefore(start, end)) {
        return null;
      }

      const startDriveTimeMinutes = resource.interVisitDriveTime || resource.startOfShiftDriveTime || 0;
      const startWithDriveTime = new Date(start.getTime() - startDriveTimeMinutes * 60 * 1000);
      const endWithDriveTime = new Date(end.getTime() + (resource.endOfShiftDriveTime || 0) * 60 * 1000);

      return {
        startTime: startWithDriveTime,
        endTime: endWithDriveTime,
      };
    })
  );
};

const toTimeRangesFromCalendarBlocks = (calendarBlocks: LocalCalendarBlock[]): TimeRange[] => {
  return compact(
    calendarBlocks.map((block) => {
      const start = parseISO(block.startTime);
      const end = parseISO(block.endTime);
      if (!isValid(start) || !isValid(end)) return null;

      const isNonBlocking = calendarBlockIsFilter(block);
      const startDriveTimeMinutes = block.interVisitDriveTime || block.startOfShiftDriveTime || 0;
      
      // Filter-only blocks without any approach drive time do not consume availability.
      if (isNonBlocking && !startDriveTimeMinutes) {
        return null;
      }

      const startWithDriveTime = new Date(start.getTime() - startDriveTimeMinutes * 60 * 1000);
      // For filter blocks, only consider the drive time to the start of the block.  For non-filter blocks, treat it like a visit.
      const endAnchor = isNonBlocking ? start : end;
      const endDriveTimeMinutes = isNonBlocking ? 0 : (block.endOfShiftDriveTime || 0);

      const endWithDriveTime = new Date(endAnchor.getTime() + endDriveTimeMinutes * 60 * 1000);

      return {
        startTime: startWithDriveTime,
        endTime: endWithDriveTime,
      };
    })
  );
};

const countHourLongGaps = (ranges: TimeRange[], shift: LocalShift, visibleDayBounds: TimeRange) => {
  const shiftStart = parseISO(shift.startTime);
  const shiftEnd = parseISO(shift.endTime);

  if (!isValid(shiftStart) || !isValid(shiftEnd)) return 0;

  const trimmedShift = trimRangeToBounds(
    { startTime: shiftStart, endTime: shiftEnd },
    visibleDayBounds
  );

  if (!trimmedShift) return 0;

  const sortedRanges = [...ranges]
    .map((range) => trimRangeToBounds(range, trimmedShift))
    .filter((range): range is TimeRange => !!range)
    .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

  if (!sortedRanges.length) {
    return getHourSlotsInRange(trimmedShift);
  }

  let hourLongGaps = 0;
  let latestEndTime = trimmedShift.startTime;

  for (const range of sortedRanges) {
    hourLongGaps += getHourSlotsInRange({
      startTime: latestEndTime,
      endTime: range.startTime,
    });

    if (range.endTime > latestEndTime) {
      latestEndTime = range.endTime;
    }
  }

  hourLongGaps += getHourSlotsInRange({
    startTime: latestEndTime,
    endTime: trimmedShift.endTime,
  });

  return hourLongGaps;
};

// TODO: This doesn't count overlaps with filtering blocks where the visit is not allowed in the block.
const countVisitOverlaps = (
  visitResources: LocalVisitResource[],
  blocks: LocalCalendarBlock[]
): VisitResourceOverlapDetail[] => {
  const visitIntervals = compact(
    map(visitResources, (visitResource) => {
      const start = parseISO(visitResource.startTime);
      const end = parseISO(visitResource.endTime);

      if (!isValid(start) || !isValid(end) || start >= end) {
        return null;
      }

      return { start, end, visitResource };
    })
  );

  const blockIntervals = compact(
    map(blocks, (block) => {
      const start = parseISO(block.startTime);
      const end = parseISO(block.endTime);

      if (!isValid(start) || !isValid(end) || start >= end) {
        return null;
      }

      return { start, end, block };
    })
  );

  const overlaps: VisitResourceOverlapDetail[] = [];

  // Visit-to-visit overlaps
  for (let i = 0; i < visitIntervals.length; i++) {
    const a = visitIntervals[i];
    for (let j = i + 1; j < visitIntervals.length; j++) {
      const b = visitIntervals[j];

      if (a.start < b.end && b.start < a.end) {
        overlaps.push({
          visitResource: a.visitResource,
          overlappingVisitResource: b.visitResource,
        });
      }
    }
  }

  // Visit-to-calendar-block overlaps
  for (let i = 0; i < visitIntervals.length; i++) {
    const visitInterval = visitIntervals[i];

    for (let j = 0; j < blockIntervals.length; j++) {
      const block = blockIntervals[j];

      if (visitInterval.start < block.end && block.start < visitInterval.end) {
        overlaps.push({
          visitResource: visitInterval.visitResource,
          overlappingCalendarBlock: block.block,
        });
      }
    }
  }

  return overlaps;
};

const consolidateVisitResourceIssues = (
  skippedVisitResources: SkippedVisitResource[] | undefined,
  allNewOverlaps: VisitResourceOverlapDetail[],
  newVisitEventsWarnings: Array<{ visitResource: LocalVisitResource; warning: string }>,
  orgConfig: OrgConfig
): ConsolidatedVisitResourceIssue[] => {
  const visitResourceIssuesMap = new Map<number, VisitResourceIssue>();

  // Add skipped visit resources
  skippedVisitResources?.forEach((skipped) => {
    if (!visitResourceIssuesMap.has(skipped.id)) {
      visitResourceIssuesMap.set(skipped.id, {
        visitResource: skipped,
        overlaps: [],
        skipped: [],
        warnings: [],
      });
    }
    const issue = visitResourceIssuesMap.get(skipped.id)!;
    if (skipped.skippedReason) {
      issue.skipped.push(`Skipped: ${skipped.skippedReason}`);
    }
  });

  // Add overlaps
  allNewOverlaps.forEach((overlap) => {
    if (!visitResourceIssuesMap.has(overlap.visitResource.id)) {
      visitResourceIssuesMap.set(overlap.visitResource.id, {
        visitResource: overlap.visitResource,
        overlaps: [],
        skipped: [],
        warnings: [],
      });
    }
    const issue = visitResourceIssuesMap.get(overlap.visitResource.id)!;
    
    if (overlap.overlappingVisitResource) {
      const overlappingVisitResource = overlap.overlappingVisitResource;
      const visitTypeName = overlappingVisitResource.visit.visitTypeId 
        ? orgConfig.visitTypes[overlappingVisitResource.visit.visitTypeId]?.name 
        : 'Unknown';
      const patient = overlappingVisitResource.visit.patient;
      const patientName = patient 
        ? `${patient.firstName} ${patient.lastName}` 
        : 'Unknown patient';
      issue.overlaps.push(`Overlaps with visit (${visitTypeName}) for patient ${patientName}`);
    }
    if (overlap.overlappingCalendarBlock) {
      issue.overlaps.push(`Overlaps with Calendar Block ID: ${overlap.overlappingCalendarBlock.id}`);
    }
  });

  // Add warnings
  newVisitEventsWarnings.forEach((item) => {
    if (!item) return;
    const { visitResource, warning } = item;
    if (!visitResourceIssuesMap.has(visitResource.id)) {
      visitResourceIssuesMap.set(visitResource.id, {
        visitResource,
        overlaps: [],
        skipped: [],
        warnings: [],
      });
    }
    const issue = visitResourceIssuesMap.get(visitResource.id)!;
    if (warning) {
      issue.warnings.push(`Warning: ${warning}`);
    }
  });

  return Array.from(visitResourceIssuesMap.values()).map((issue) => {
    // Determine main issue and additional count
    const allReasons = issue.skipped.length > 0 ? [...issue.skipped] : [...issue.overlaps, ...issue.warnings];
    let mainText = '';
    let additionalCount = 0;

    if (issue.skipped.length > 0) {
      // If a visit is skipped, that should be the only issue
      issue.overlaps = [];
      issue.warnings = [];
      mainText = issue.skipped[0];
      additionalCount = issue.skipped.length - 1;
    } else if (issue.overlaps.length > 0) {
      mainText = 'Major Overlap';
      additionalCount = issue.warnings.length;
    } else if (issue.warnings.length > 0) {
      mainText = 'Warnings';
      additionalCount = 0;
    }
    
    return {
      ...issue,
      allReasons,
      mainText,
      additionalCount,
    };
  });
};

export const getOptimizationIssueStats = ({
  date,
  originalVisitResources,
  localVisitResources,
  localVisits,
  localVisitRequests,
  providers,
  localShifts,
  localCalendarBlocks,
  careRequirementVisits,
  skippedVisitResources,
  orgConfig,
}: OptimizationIssueStatsInput): OptimizationIssueStats => {
  const selectedDate = castDate(date);
  const visibleDayBounds = {
    startTime: startOfDay(selectedDate),
    endTime: addDays(startOfDay(selectedDate), 1),
  };

  const careRequirementWarningsByVisitResourceId = buildCareRequirementWarningsByVisitResourceId(localVisitResources, careRequirementVisits)

  const isOptimized = !!originalVisitResources?.length;

  const totalOptimizedVisits = uniq(originalVisitResources?.map((vr) => vr.visit.id))?.length;

  // if no originalVisitResources assigned then nothing was optimized therefore no stats should be calculated
  const activeLocalVisitResources = isOptimized ? localVisitResources.filter((vr) => visitResourceStatus(vr) !== VisitEventStatusEnum.Canceled) : [];

  const activeOriginalVisitResources = isOptimized ? activeLocalVisitResources.map((vr) => {
    const original = originalVisitResources.find((o) => o.id === vr.id);
    if (original) {
      return {
        ...vr,
        ...original
      }
    }

    return vr;
  }) : [];

  const newTotalDriveTime = sumBy(activeLocalVisitResources, (vr) => {
    return vr.interVisitDriveTime + vr.startOfShiftDriveTime + vr.endOfShiftDriveTime;
  });

  const originalTotalDriveTime = sumBy(activeOriginalVisitResources, (vr) => {
    return vr.interVisitDriveTime + vr.startOfShiftDriveTime + vr.endOfShiftDriveTime;
  });

  let newHourLongGaps = 0;
  let originalHourLongGaps = 0;
  const allNewOverlaps: VisitResourceOverlapDetail[] = [];

  const originalVisitsWithIssues = new Set<number>();
  const newVisitsWithIssues = new Set<number>();

  localShifts.forEach((shift) => {
    if (shift.status === 'canceled' || !shift?.provider?.active) return;

    const localShiftVisitResources = getShiftBlockers(activeLocalVisitResources, shift);
    const originalShiftVisitResources = getShiftBlockers(activeOriginalVisitResources, shift);
    const shiftCalendarBlocks = getShiftBlockers(localCalendarBlocks, shift);

    const calendarBlockRanges = toTimeRangesFromCalendarBlocks(shiftCalendarBlocks);
    const localVisitRanges = toTimeRangesFromVisitResources(localShiftVisitResources);
    const originalVisitRanges = toTimeRangesFromVisitResources(originalShiftVisitResources);

    newHourLongGaps += countHourLongGaps([...localVisitRanges, ...calendarBlockRanges], shift, visibleDayBounds);
    originalHourLongGaps += countHourLongGaps([...originalVisitRanges, ...calendarBlockRanges], shift, visibleDayBounds);

    const blockingCalendarBlocks = shiftCalendarBlocks.filter((cb) => !calendarBlockIsFilter(cb));
    const newOverlaps = countVisitOverlaps(localShiftVisitResources, blockingCalendarBlocks);
    const originalOverlaps = countVisitOverlaps(originalShiftVisitResources, blockingCalendarBlocks);
    originalOverlaps.forEach(overlap => originalVisitsWithIssues.add(overlap.visitResource.visitId));
    allNewOverlaps.push(...newOverlaps);
  });

  const originalCareRequirementWarningsByVisitResourceId = buildCareRequirementWarningsByVisitResourceId(
    activeOriginalVisitResources,
    careRequirementVisits,
  );

  const originalVisitEventsWarnings = compact(activeOriginalVisitResources?.map((vr) => {
    const event = createVisitEventFromResource(
      vr,
      null,
      false,
      new Set(),
      false,
      undefined,
      orgConfig,
    );
    const warning = getVisitWarning(
      event,
      localShifts,
      localVisitResources,
      localVisits,
      localVisitRequests,
      localCalendarBlocks,
      providers,
      orgConfig,
      {},
      originalCareRequirementWarningsByVisitResourceId
    );
    if (!warning) return null;
    return {
      visitResource: vr,
      warning,
    };
  }));
  originalVisitEventsWarnings.forEach(warning => {
    originalVisitsWithIssues.add(warning.visitResource.visitId);
  })

  // get warnings from new optimized visit resources: e.g: no shift, inactive provider, etc.
  const newVisitEventsWarnings = compact(activeLocalVisitResources?.map((vr) => {
    const event = createVisitEventFromResource(
      vr,
      null,
      false,
      new Set(),
      false,
      undefined,
      orgConfig,
    );
    const warning = getVisitWarning(
      event,
      localShifts,
      localVisitResources,
      localVisits,
      localVisitRequests,
      localCalendarBlocks,
      providers,
      orgConfig,
      {},
      careRequirementWarningsByVisitResourceId
    )
    if (!warning) return null;
    return {
      visitResource: vr,
      warning,
    };
  }));


  // Consolidate skippedVisitResources, overlaps, and warnings by unique visitResource
  const consolidatedVisitResourceIssues = consolidateVisitResourceIssues(
    skippedVisitResources,
    allNewOverlaps,
    newVisitEventsWarnings,
    orgConfig
  );

  consolidatedVisitResourceIssues.forEach(issue => {
    newVisitsWithIssues.add(issue.visitResource.visitId);
  })

  return {
    totalOptimizedVisits,
    originalVisitsWithIssuesCount: originalVisitsWithIssues.size,
    newVisitsWithIssuesCount: newVisitsWithIssues.size,
    originalTotalDriveTime,
    newTotalDriveTime,
    originalAvailable60Min: isOptimized ? originalHourLongGaps : "-",
    newAvailable60Min: isOptimized ? newHourLongGaps : "-",
    consolidatedVisitResourceIssues,
  };
};

export const shouldKeepOptimizationResult = (stats: OptimizationIssueStats): boolean => {
  if (stats.newVisitsWithIssuesCount < stats.originalVisitsWithIssuesCount) {
    return true;
  }

  if (stats.newVisitsWithIssuesCount > stats.originalVisitsWithIssuesCount) {
    return false;
  }

  return stats.newTotalDriveTime <= stats.originalTotalDriveTime;
};

export const shouldWaitForOptimizationDriveTimes = ({
  requiresDriveTimeUpdate,
  driveTimesLoading,
  pendingDriveTimeShiftCount,
  hasPendingDriveTimeUpdates,
}: {
  requiresDriveTimeUpdate: boolean;
  driveTimesLoading: boolean;
  pendingDriveTimeShiftCount: number;
  hasPendingDriveTimeUpdates: boolean;
}): boolean => {
  if (!requiresDriveTimeUpdate) return false;
  return driveTimesLoading || pendingDriveTimeShiftCount > 0 || hasPendingDriveTimeUpdates;
};

export const shouldResetOptimizationResult = (
  stats: OptimizationIssueStats,
  ignoreShouldKeepOptimizationResult = false,
): boolean => {
  if (ignoreShouldKeepOptimizationResult) {
    return false;
  }

  return !shouldKeepOptimizationResult(stats);
};
